import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api, formatError } from "@/lib/api";
import { getPublicBase } from "@/lib/share";
import { localizeAsset } from "@/lib/assets";
import { Loader } from "@/components/AppShell";
import { toast } from "sonner";
import { ArrowLeft, Heart, PenLine, Flame, X, LifeBuoy, Share2 } from "lucide-react";

const SECTIONS = ["evokes", "accompanies", "question", "gesture"];

export default function SymbolDetail() {
  const { key } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sym, setSym] = useState(undefined);
  const [fav, setFav] = useState(false);
  const [compose, setCompose] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [crisis, setCrisis] = useState(false);

  useEffect(() => {
    const src = preview
      ? api.get("/admin/symbols").then((r) => r.data.find((s) => s.key === key) || null)
      : api.get(`/symbols/${key}`).then((r) => r.data).catch(() => null);
    src.then(setSym).catch(() => setSym(null));
    api.get("/symbols/favorites").then((r) => setFav((r.data.keys || []).includes(key))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, preview]);

  if (sym === undefined) return <Loader />;
  if (sym === null) {
    return (
      <div className="anima-backdrop min-h-screen flex items-center justify-center px-7 text-center">
        <div>
          <p className="font-serif text-2xl text-anima-ink mb-4">{t("symdetail.notFound")}</p>
          <button onClick={() => navigate("/app/simboli")} className="rounded-full bg-anima-sagedeep text-anima-ivory px-6 py-3" data-testid="symdetail-notfound-back">{t("symdetail.backToSymbols")}</button>
        </div>
      </div>
    );
  }

  const toggleFav = async () => {
    try {
      if (fav) { await api.delete(`/symbols/favorites/${key}`); setFav(false); toast.success(t("symdetail.removedFav")); }
      else { await api.post(`/symbols/favorites/${key}`); setFav(true); toast.success(t("symdetail.savedFav")); }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const curQ = (sym && (sym.current_question || (sym.questions && sym.questions[0]) || sym.question)) || "";
  const curP = (sym && (sym.current_practice || (sym.practices && sym.practices[0]) || sym.gesture)) || "";

  const saveJournal = async () => {
    if (!text.trim()) { toast.error(t("symdetail.writeFirst")); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/symbols/journal", { symbol_key: key, response: text.trim(), question: curQ });
      if (data.crisis) { setCrisis(true); setSaving(false); return; }
      toast.success(t("symdetail.savedJournal"));
      setCompose(false); setText("");
      navigate("/app/diario");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setSaving(false); }
  };

  const cancelCompose = () => { setCompose(false); setText(""); setCrisis(false); };

  const shareSymbol = async () => {
    const phrase = (sym && (sym.phrase || curQ || sym.meaning)) || "";
    const base = await getPublicBase();
    const url = `${base}/app/simboli/${key}`;
    const text = `${sym.name}${phrase ? " — " + phrase : ""}\n\n— Ascolta Anima`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${sym.name} · Ascolta Anima`, text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success(t("share.copied"));
      }
    } catch (err) { console.debug(tx("Share dismissed:"), err?.message || err); }
  };

  return (
    <div className="anima-backdrop min-h-screen w-full flex justify-center">
      <div className="w-full max-w-md px-7 py-10">
        <button onClick={() => navigate(-1)} className="text-anima-muted mb-7 flex items-center gap-1 text-base" data-testid="symdetail-back">
          <ArrowLeft size={16} /> {t("common.back")}
        </button>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="w-full rounded-3xl overflow-hidden border border-anima-ink/10 bg-anima-sage/10">
            <img
              src={localizeAsset(sym.image_url)}
              alt={sym.name}
              className="w-full h-auto block"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              data-testid="symdetail-image"
            />
          </div>
        </motion.div>

        <div className="flex items-start justify-between gap-3 mt-6">
          <h1 className="font-serif text-[40px] leading-none text-anima-ink" data-testid="symdetail-name">{sym.name}</h1>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={shareSymbol}
              className="w-11 h-11 rounded-full flex items-center justify-center border border-anima-ink/10 bg-white/55 text-anima-muted"
              data-testid="symdetail-share-btn"
              aria-label={t("share.action")}
            >
              <Share2 size={18} />
            </button>
            <button
              onClick={toggleFav}
              aria-pressed={fav}
              className={`w-11 h-11 rounded-full flex items-center justify-center border transition-colors duration-200 ${fav ? "bg-anima-pink/70 border-anima-pink" : "bg-white/55 border-anima-ink/10"}`}
              data-testid="symdetail-fav-btn"
              aria-label={t("symdetail.saveFav")}
            >
              <Heart size={19} className={fav ? "fill-anima-ink/70 text-anima-ink/70" : "text-anima-muted"} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {sym.keywords.slice(0, 3).map((k) => (
            <span key={k} className="rounded-full bg-anima-pink/55 text-anima-ink text-[13px] px-3.5 py-1.5">{k}</span>
          ))}
        </div>

        <div className="mt-8 space-y-7">
          {SECTIONS.map((field) => {
            const val = field === "question" ? curQ : field === "gesture" ? curP : sym[field];
            return val ? (
              <section key={field} data-testid={`symdetail-${field}`}>
                <h2 className="font-serif text-2xl text-anima-ink leading-snug mb-2">{t(`symdetail.${field}`)}</h2>
                <p className={`text-base text-anima-ink/85 leading-relaxed ${field === "question" ? "italic" : ""}`}>{val}</p>
              </section>
            ) : null;
          })}
        </div>

        {sym.key === "fuoco_spirito" && (
          <section className="mt-9 rounded-3xl bg-anima-purple/25 border border-anima-ink/5 p-6" data-testid="symdetail-ianas">
            <div className="flex items-center gap-2 mb-2">
              <Flame size={16} className="text-anima-golddeep" />
              <h2 className="font-serif text-xl text-anima-ink leading-tight">{t("symdetail.ianasTitle")}</h2>
            </div>
            <p className="text-[15px] text-anima-ink/85 leading-relaxed">
              {t("symdetail.ianasBody")}
            </p>
            <button onClick={() => navigate("/ianas")} className="mt-4 text-[15px] text-anima-sagedeep font-medium" data-testid="symdetail-ianas-link">
              {t("symdetail.ianasLink")}
            </button>
          </section>
        )}

        <div className="mt-9 space-y-3">
          <button
            onClick={() => setCompose(true)}
            className="w-full rounded-full bg-anima-sagedeep text-anima-ivory py-4 flex items-center justify-center gap-2 text-base active:scale-[0.98] transition-transform duration-200"
            data-testid="symdetail-journal-btn"
          >
            <PenLine size={18} /> {t("symdetail.writeInDiary")}
          </button>
          <button
            onClick={toggleFav}
            aria-pressed={fav}
            className="w-full rounded-full border border-anima-ink/15 text-anima-ink py-4 flex items-center justify-center gap-2 text-base"
            data-testid="symdetail-fav-btn-2"
          >
            <Heart size={18} className={fav ? "fill-anima-ink/70" : ""} /> {fav ? t("symdetail.removeFav") : t("symdetail.saveFav")}
          </button>
        </div>

        <div className="h-6" />
      </div>

      {compose && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-anima-ink/35 backdrop-blur-sm px-4 py-4" onClick={cancelCompose}>
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-anima-ivory rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
            data-testid="symdetail-compose"
          >
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] uppercase tracking-widest text-anima-golddeep">{t("symdetail.composePrefix")} · {sym.name}</p>
              <button onClick={cancelCompose} data-testid="symdetail-compose-cancel"><X size={20} className="text-anima-muted" /></button>
            </div>
            <p className="font-serif text-2xl text-anima-ink leading-snug mb-4">{curQ || t("symdetail.writeInDiary")}</p>

            {crisis ? (
              <div className="rounded-2xl bg-anima-pink/40 border border-anima-pink p-5" data-testid="symdetail-crisis">
                <div className="flex items-center gap-2 text-anima-ink mb-2"><LifeBuoy size={18} /> <span className="font-medium">{t("symdetail.crisisTitle")}</span></div>
                <p className="text-sm text-anima-ink/85 leading-relaxed">
                  {t("symdetail.crisisBody")}
                </p>
                <button onClick={cancelCompose} className="mt-4 w-full rounded-full border border-anima-ink/15 text-anima-ink py-3" data-testid="symdetail-crisis-close">{t("symdetail.crisisClose")}</button>
              </div>
            ) : (
              <>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={6}
                  placeholder={t("home.writePlaceholder")}
                  className="w-full rounded-2xl bg-white/60 border border-anima-ink/12 px-4 py-3 outline-none text-base leading-relaxed resize-none"
                  data-testid="symdetail-compose-text"
                  autoFocus
                />
                <div className="flex gap-3 mt-4">
                  <button onClick={cancelCompose} className="flex-1 rounded-full border border-anima-ink/15 text-anima-ink py-3" data-testid="symdetail-compose-cancel-2">{t("common.cancel")}</button>
                  <button onClick={saveJournal} disabled={saving} className="flex-1 rounded-full bg-anima-sagedeep text-anima-ivory py-3 disabled:opacity-60" data-testid="symdetail-compose-save">
                    {saving ? t("symdetail.saving") : t("symdetail.saveJournal")}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
